import React, { useState, useEffect } from "react";
import { AlertColor, Box, Button } from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/store/Store";
import MWExceptionList from "../../../component/MWExceptionList";
import {
  GetPatientCaseNoteListBody,
  NoteList,
} from "../../../redux/Notes/PatientCaseNoteList/Model";
import dayjs from "dayjs";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import { getPatientCaseNoteList } from "../../../redux/Notes/PatientCaseNoteList/API";
import MWSnackbar from "../../../component/MWSnackbar";
import {
  clearNoteAPIRes,
  updateNote,
} from "../../../redux/Notes/updateNoteStatus/API";
import { t } from "i18next";

type Props = {
  inactiveNoteLoader?: boolean | any;
};

export default function ActivePatientCaseNotes({ inactiveNoteLoader }: Props) {
  const { patientid } = useParams() as {
    patientid: string;
  };
  const { caseid } = useParams() as {
    caseid: string;
  };
  const dispatch = useDispatch();

  const getNoteListBody = {
    patientId: patientid,
    caseId: caseid,
    isActive: false,
  } as GetPatientCaseNoteListBody;

  // Inactive Notes List Api Value from store
  const NoteListFromAPI = useSelector(
    (state: AppState) => state.patientCaseNoteListRes
  );
  const [adminNoteList, setAdminNoteList] = useState([] as any[]);
  const [apiRes, setAPIRes] = useState("");
  const [snackBarType, setSnackBarType] = useState<AlertColor>("success");
  useEffect(() => {
    inactiveNoteLoader(NoteListFromAPI?.loading);
    if (NoteListFromAPI?.errorMsg !== "") {
      setAPIRes(NoteListFromAPI?.errorMsg);
      setSnackBarType("error");
    }
    if (NoteListFromAPI?.getPatientCaseNoteListRes?.noteList !== undefined) {
      let noteListItem =
        NoteListFromAPI?.getPatientCaseNoteListRes?.noteList.map(
          (element: NoteList) => {
            return {
              id: element.id,
              note: element?.note !== undefined ? element?.note : "",
              createdDate:
                element?.createdDate !== undefined
                  ? dayjs(element?.createdDate).format("ddd, DD/MM/YYYY")
                  : "",
              createBy:
                element?.createdBy === undefined
                  ? ""
                  : element?.createdBy?.user?.first_name +
                    " " +
                    element?.createdBy?.user?.last_name,
              modifiedDate:
                element?.modifiedDate !== undefined
                  ? dayjs(element?.modifiedDate).format("ddd, DD/MM/YYYY")
                  : "",
              modifiedBy:
                element?.modifiedBy === undefined
                  ? ""
                  : element?.modifiedBy?.user?.first_name +
                    " " +
                    element?.modifiedBy?.user?.last_name,
            };
          }
        );
      setAdminNoteList(noteListItem);
    } else setAdminNoteList([] as any[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [NoteListFromAPI]);

  const columns: GridColDef[] = [
    { field: "note", headerName: `${t("common.note", { ns: ["home"] })!}`, flex: 1 },
    { field: "createdDate", headerName: `${t("admindashboardpage.createDate", { ns: ["home"] })!}`, minWidth: 140 },
    {
      field: "createBy",
      headerName: `${t("common.createdBy", { ns: ["home"] })!}`,
      flex: 1,
    },
    { field: "modifiedBy", headerName: `${t("common.modifyBy", { ns: ["home"] })!}`, flex: 1 },
    { field: "modifiedDate", headerName: `${t("appointmentpage.modifieddate", { ns: ["home"] })!}`, minWidth: 140 },
    {
      field: "id",
      headerName: `${t("docnotelist.modifiedstatus", { ns: ["home"] })!}`,
      minWidth: 35,
      renderCell: (params: GridCellParams) => (
        <Button
          startIcon={<LoopIcon />}
          variant="outlined"
          onClick={() => {
            toggleNoteStatus(params.value);
          }}
          sx={{ textTransform: "none" }}
          disabled={updateNotesVal?.loading}
        >
          {t("userprofilepage.edit", { ns: ["home"] })!}
        </Button>
      ),
    },
  ];

  // Notes Status change API
  function toggleNoteStatus(response: number) {
    dispatch(updateNote(response));
  }
  // update Notes Api Value from store
  const updateNotesVal = useSelector((state: AppState) => state.updateNoteRes);
  useEffect(() => {
    inactiveNoteLoader(updateNotesVal?.loading);
    if (updateNotesVal?.errorMsg !== "") {
      setAPIRes(updateNotesVal?.errorMsg);
      setSnackBarType("error");
    } else if (updateNotesVal?.updateNoteResRes?.message !== undefined) {
      setAPIRes(updateNotesVal?.updateNoteResRes?.message);
      dispatch(getPatientCaseNoteList(getNoteListBody));
      setSnackBarType("success");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNotesVal]);

  useEffect(() => {
    dispatch(getPatientCaseNoteList(getNoteListBody));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const checkAlertClose = (passedVal: boolean) => {
    if (passedVal) {
      dispatch(clearNoteAPIRes());
    }
  };
  return (
    <Box>
      {adminNoteList.length > 0 ? (
        <DataGrid
          rows={adminNoteList}
          columns={columns}
          pageSize={10}
          hideFooter
          hideFooterPagination
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          autoHeight
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 250 },
            },
          }}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
        />
      ) : (
        <MWExceptionList />
      )}
      <MWSnackbar
        msg={apiRes}
        type={snackBarType}
        alertClose={checkAlertClose}
      />
    </Box>
  );
}
